/**
 * Modelling Session client — load / save / revise a season-programme session and
 * its month x day-type slices against /api/research/sessions (db/75, §5c).
 *
 * The API stores JSON blobs; these helpers keep the wire shape thin and let the
 * caller parse `state_json` / `result_json` per slice. `seasonRollup` is the
 * no-garbage guard (MASTER_PLAN §6.3): only DESIGNED slices count, running-days
 * are real (never assumed), and partial coverage is flagged rather than hidden.
 */
import type {
  CostModel, ModellingSessionFull, ModellingSessionMeta, SessionSlice, StudioMode,
} from './types'

const JSON_HEADERS = { 'content-type': 'application/json' }

async function readJson(r: Response): Promise<Record<string, unknown>> {
  return (await r.json().catch(() => ({}))) as Record<string, unknown>
}
function fail(data: Record<string, unknown>, r: Response, what: string): Error {
  return new Error(typeof data.error === 'string' ? data.error : `${what} failed (${r.status})`)
}

/**
 * GET with retries. Session reads are idempotent, and the SWA functions host
 * occasionally serves a burst of 5xx while a worker recycles (2026-08-24: one
 * such burst turned "Open session" into a hard error banner across the whole
 * Studio). Three attempts with backoff ride out a blip; a real outage still
 * surfaces the server's error after the last attempt. Never used for writes.
 */
async function getWithRetry(url: string): Promise<Response> {
  let lastResp: Response | null = null
  let lastErr: unknown = null
  for (let attempt = 0; attempt < 3; attempt++) {
    if (attempt > 0) await new Promise((res) => setTimeout(res, 800 * attempt * attempt))
    try {
      const r = await fetch(url)
      if (r.status < 500) return r
      lastResp = r
    } catch (e) {
      lastErr = e
    }
  }
  if (lastResp) return lastResp
  throw lastErr instanceof Error ? lastErr : new Error(String(lastErr))
}

export async function listSessions(season?: string): Promise<ModellingSessionMeta[]> {
  const q = season ? `?season=${encodeURIComponent(season)}` : ''
  const r = await getWithRetry(`/api/research/sessions${q}`)
  const data = await readJson(r)
  if (!r.ok) throw fail(data, r, 'list sessions')
  return (data.sessions as ModellingSessionMeta[]) ?? []
}

export interface LoadedSession { session: ModellingSessionFull; slices: SessionSlice[] }

export async function loadSession(id: number): Promise<LoadedSession> {
  const r = await getWithRetry(`/api/research/sessions/${id}`)
  const data = await readJson(r)
  if (!r.ok) throw fail(data, r, 'load session')
  return { session: data.session as ModellingSessionFull, slices: (data.slices as SessionSlice[]) ?? [] }
}

export interface SessionInput {
  name: string
  season: string
  question?: string
  status?: string
  months?: number[]
  modes?: string[]
  cost_model?: CostModel
  running_set?: unknown
  calibration?: unknown
  /** {mode:'season'} | {mode:'range',from,to} — the Brief's modelling period. */
  period?: unknown
  narrative?: string
  active_stage?: string
  data_as_at?: string
  /** Optional note stored with the Version snapshot on update. */
  version_note?: string
}

export async function createSession(input: SessionInput): Promise<number> {
  const r = await fetch('/api/research/sessions', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(input) })
  const data = await readJson(r)
  if (!r.ok) throw fail(data, r, 'create session')
  return data.id as number
}

export async function updateSession(id: number, input: SessionInput): Promise<void> {
  const r = await fetch(`/api/research/sessions/${id}`, { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify(input) })
  if (!r.ok) throw fail(await readJson(r), r, 'update session')
}

/** Name + question only — the pen in the Open… list (PATCH; touches nothing else). */
export async function renameSession(id: number, name: string, question: string | null): Promise<void> {
  const r = await fetch(`/api/research/sessions/${id}`, { method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify({ name, question }) })
  if (!r.ok) throw fail(await readJson(r), r, 'rename session')
}

export async function deleteSession(id: number): Promise<void> {
  const r = await fetch(`/api/research/sessions/${id}`, { method: 'DELETE' })
  if (!r.ok && r.status !== 204) throw fail(await readJson(r), r, 'delete session')
}

export interface SliceInput {
  month_no: number
  day_class: string
  mode: StudioMode
  state: unknown
  result?: unknown
  status?: string
}

export async function saveSlice(sessionId: number, slice: SliceInput): Promise<void> {
  const r = await fetch(`/api/research/sessions/${sessionId}/slice`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(slice) })
  if (!r.ok) throw fail(await readJson(r), r, 'save slice')
}

export interface SessionVersionMeta { id: number; note: string | null; created_by: string; created_at: string }

export async function listVersions(sessionId: number): Promise<SessionVersionMeta[]> {
  const r = await fetch(`/api/research/sessions/${sessionId}/versions`)
  const data = await readJson(r)
  if (!r.ok) throw fail(data, r, 'list versions')
  return (data.versions as SessionVersionMeta[]) ?? []
}

export interface RollupSlice {
  status: string
  /** Per-operating-day contribution for this slice (null = not scored). */
  contributionPerDay: number | null
  /** Real running days for this slice from the applied RunningTimetableSet (null = none applied). */
  runningDays: number | null
}

export interface RollupResult {
  /** Σ over DESIGNED, fully-specified slices of contributionPerDay × runningDays. */
  total: number
  designedCount: number
  /** True if any slice was skipped (not designed, or missing a running-day count). */
  partial: boolean
  /** Designed slices excluded because they had no running-day count or no score. */
  excluded: number
}

/**
 * Season roll-up — the no-garbage guard. Only BUILT slices contribute, each
 * multiplied by its REAL running-day count (never an assumed one). A designed
 * slice missing a running-day count or a score contributes nothing and is
 * counted as excluded; any incompleteness sets `partial` so the UI can label the
 * figure "across N designed slices" rather than present it as the whole season.
 */
/** A slice the season build banked — 'in_progress' is the legacy name for a
 *  built month where the Brief bound (before 2026-09-04); it is a built month. */
export const builtSlice = (s: { status: string }): boolean => s.status === 'designed' || s.status === 'in_progress'

export function seasonRollup(slices: RollupSlice[]): RollupResult {
  let total = 0, designedCount = 0, excluded = 0, partial = false
  for (const s of slices) {
    if (!builtSlice(s)) { partial = true; continue }
    designedCount += 1
    if (s.contributionPerDay == null || s.runningDays == null) { excluded += 1; partial = true; continue }
    total += s.contributionPerDay * s.runningDays
  }
  return { total, designedCount, partial, excluded }
}
